import React, { useState, useEffect } from 'react';
import { Plus, Eye, IndianRupee, Users, ArrowLeft, CheckCircle, Clock, AlertTriangle, ChevronDown, ChevronUp, X, Download } from 'lucide-react';
import ReportService from '../services/ReportService';
import Table from '../components/ui/Table';
import Loader from '../components/ui/Loader';
import CreateFeesModal from '../components/fees/CreateFeesModal';
import FeeDetailModal from '../components/fees/FeeDetailModal';
import PaymentTypeModal from '../components/fees/PaymentTypeModal';
import { useToast } from '../contexts/ToastContext';
import { feesService } from '../services/feesService';
import { classService } from '../services/classService';
import { useSelector } from 'react-redux';

const Fees = () => {
  const { success, error } = useToast();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [pendingFeeUpdate, setPendingFeeUpdate] = useState(null);
  const { user } = useSelector((state) => state.auth);

  const [selectedAcademicYearFilter, setSelectedAcademicYearFilter] = useState('');
  const [selectedGenderFilter, setSelectedGenderFilter] = useState('');
  const [nameSort, setNameSort] = useState('a-z');

  const loadClasses = async () => {
    try {
      const classesData = await classService.getAllClasses(user.token);
      setClasses(classesData);
    } catch (err) {
      console.error('Error loading classes:', err);
      error('Failed to load classes');
    }
  };

  const loadStudentsWithFees = async () => {
    if (!selectedClass) return;
    
    setIsLoading(true);
    try {
      const response = await feesService.getStudentsWithFeeDetails(
        selectedClass._id,
        selectedAcademicYearFilter,
        user.token
      );
      
      if (response.success) {
        setStudents(response.students || []);
      } else {
        error(response.message || 'Failed to load students with fees');
        setStudents([]);
      }
    } catch (err) {
      console.error('Error loading students with fees:', err);
      error('Failed to load students with fees: ' + err.message);
      setStudents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStatistics = async () => {
    if (!selectedClass) return;
    
    try {
      const response = await feesService.getFeesStatistics(
        selectedClass._id,
        selectedAcademicYearFilter,
        user.token
      );
      if (response.success) {
        setStatistics(response.statistics);
      }
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  };

  // Load classes
  useEffect(() => {
    loadClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.token]);

  // Load students with fees when class is selected
  useEffect(() => {
    if (selectedClass) {
      loadStudentsWithFees();
      loadStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, selectedAcademicYearFilter]);

  const handleClassClick = (cls, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    // Use a callback to ensure state is set immediately
    setSelectedClass(() => cls);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const handleBackToClasses = () => {
    setSelectedClass(null);
    setStudents([]);
    setStatistics(null);
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleUpdateFeeStatusClick = (feeId, feeInfo, newStatus) => {
    if (newStatus === 'paid') {
      // Show payment type modal first
      setPendingFeeUpdate({ feeId, feeInfo, newStatus });
      setPaymentModalOpen(true);
    } else {
      // Directly update for pending/overdue
      handleUpdateFeeStatus(feeId, newStatus, null, null);
    }
  };

  const handlePaymentConfirm = async (paymentData) => {
    if (pendingFeeUpdate) {
      // If marking all fees, update all of them
      if (pendingFeeUpdate.markAll && pendingFeeUpdate.allFees) {
        const updatePromises = pendingFeeUpdate.allFees.map(fee => 
          handleUpdateFeeStatus(fee._id, 'paid', paymentData.paymentType, paymentData.transactionId)
        );
        await Promise.all(updatePromises);
      } else {
        // Single fee update
        await handleUpdateFeeStatus(
          pendingFeeUpdate.feeId,
          pendingFeeUpdate.newStatus,
          paymentData.paymentType,
          paymentData.transactionId
        );
      }
      setPaymentModalOpen(false);
      setPendingFeeUpdate(null);
    }
  };

  const handleUpdateFeeStatus = async (feeId, newStatus, paymentType = null, transactionId = null) => {
    try {
      const updateData = {
        status: newStatus,
        paymentDate: newStatus === 'paid' ? Date.now() : null,
        paymentMethod: newStatus === 'paid' ? (paymentType || 'cash') : null,
        transactionId: newStatus === 'paid' && transactionId ? transactionId : null
      };

      const response = await feesService.updateFeeStatus(feeId, updateData, user.token);
      if (response.success) {
        if (newStatus === 'paid') {
          success(`Fee marked as paid (${paymentType || 'cash'})`);
        } else {
          success('Fee status updated successfully');
        }
        await loadStudentsWithFees();
        await loadStatistics();
        return true;
      } else {
        error('Failed to update fee status');
        return false;
      }
    } catch (err) {
      console.error('Error updating fee status:', err);
      error('Failed to update fee status');
      return false;
    }
  };

  // Filter students
  const filteredStudents = students.filter(student => {
    const matchesSearch = !searchTerm || 
      student?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      student?.email?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      student?.regNo?.toLowerCase().includes(searchTerm?.toLowerCase());
    
    const matchesGender = !selectedGenderFilter || 
      student?.gender?.toLowerCase() === selectedGenderFilter.toLowerCase();
    
    return matchesSearch && matchesGender;
  }).sort((a, b) => {
    if (nameSort === 'a-z') {
      return (a?.name || '').localeCompare(b?.name || '');
    } else {
      return (b?.name || '').localeCompare(a?.name || '');
    }
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedStudents = filteredStudents.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGenderFilter, nameSort]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'overdue':
        return <AlertTriangle size={16} className="text-red-600" />;
      case 'pending':
        return <Clock size={16} className="text-yellow-600" />;
      default:
        return <Clock size={16} className="text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Classes View - Columns
  const classColumns = [
    {
      key: 'name',
      label: 'Class Details',
      render: (cls) => (
        <div>
          <p className="font-medium">{cls?.name}</p>
          <p className="text-sm text-gray-600">Section {cls?.section}</p>
        </div>
      )
    },
    {
      key: 'fees',
      label: 'Fee Structure',
      render: (cls) => (
        <div>
          <p className="text-sm font-medium text-gray-900">
            ₹{cls?.feesPerAcademicYear?.toLocaleString() || 0}
          </p>
          <p className="text-xs text-gray-600">
            {cls?.numberOfTerms || 0} Terms
          </p>
        </div>
      )
    },
    {
      key: 'enrollment',
      label: 'Enrollment',
      render: (cls) => {
        const enrollmentCount = cls?.actualStudentCount !== undefined 
          ? cls.actualStudentCount 
          : (cls?.enrolledStudents?.length || 0);
        const capacity = cls?.class_capacity || 0;
        return (
          <div className="flex items-center space-x-2">
            <Users size={16} className="text-gray-400" />
            <span>{enrollmentCount}/{capacity}</span>
          </div>
        );
      }
    },
    {
      key: 'academicYear',
      label: 'Academic Year',
      render: (cls) => (
        <div>
          {cls?.academicYearFrom && cls?.academicYearTo ? (
            <div>
              <p className="text-sm font-medium text-gray-900">
                {new Date(cls.academicYearFrom).getFullYear()}
              </p>
              <p className="text-xs text-gray-500">to</p>
              <p className="text-sm font-medium text-orange-600">
                {new Date(cls.academicYearTo).getFullYear()}
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Not set</p>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (cls) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleClassClick(cls, e);
          }}
          className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition-colors flex items-center space-x-1"
        >
          <Eye size={14} />
          <span>View Fees</span>
        </button>
      )
    }
  ];

  // Students View - Columns
  const studentColumns = [
    {
      key: 'student',
      label: 'Student',
      render: (student) => (
        <div className="flex items-center">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
            <span className="text-blue-600 font-semibold text-sm">
              {student.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </span>
          </div>
          <div>
            <p className="font-medium">{student.name}</p>
            <p className="text-sm text-gray-600">{student.email}</p>
            {student.regNo && (
              <p className="text-xs text-gray-500">Reg: {student.regNo}</p>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'feeSummary',
      label: 'Fee Summary',
      render: (student) => {
        const summary = student.feeSummary || {};
        return (
          <div>
            <p className="text-sm font-medium text-gray-900">
              Total: ₹{(summary.totalFees || 0).toLocaleString()}
            </p>
            <div className="text-xs space-y-1 mt-1">
              <p className="text-green-600">Paid: ₹{(summary.paidFees || 0).toLocaleString()}</p>
              <p className="text-yellow-600">Pending: ₹{(summary.pendingFees || 0).toLocaleString()}</p>
              {summary.overdueFees > 0 && (
                <p className="text-red-600">Overdue: ₹{(summary.overdueFees || 0).toLocaleString()}</p>
              )}
            </div>
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Overall Status',
      render: (student) => {
        const status = student.feeSummary?.overallStatus || 'pending';
        return (
          <div className="flex items-center space-x-2">
            {getStatusIcon(status)}
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          </div>
        );
      }
    },
    {
      key: 'feeRecords',
      label: 'Fee Records',
      render: (student) => {
        const feeRecords = student.feeRecords || [];
        const studentId = student._id?.toString ? student._id.toString() : student._id;
        const isExpanded = expandedStudents.has(studentId);
        const sortedFees = [...feeRecords].sort((a, b) => a.termNumber - b.termNumber);
        
        return (
          <div className="space-y-2">
            <button
              onClick={() => {
                const newExpanded = new Set(expandedStudents);
                if (isExpanded) {
                  newExpanded.delete(studentId);
                } else {
                  newExpanded.add(studentId);
                }
                setExpandedStudents(newExpanded);
              }}
              className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-700"
            >
              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              <span>{feeRecords.length} Term{feeRecords.length !== 1 ? 's' : ''}</span>
            </button>
            
            {isExpanded && (
              <div className="space-y-2 mt-2 border-t pt-2">
                {sortedFees.map((fee, idx) => (
                  <div key={idx} className="bg-gray-50 p-2 rounded border">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-700">Term {fee.termNumber}</span>
                        <span className="text-xs text-gray-500">{fee.academicYear}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        {getStatusIcon(fee.status)}
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(fee.status)}`}>
                          {fee.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">
                        Amount: <span className="font-medium">₹{(fee.amount || 0).toLocaleString()}</span>
                      </span>
                      <div className="text-right">
                        {fee.paymentDate && (
                          <div>
                            <span className="text-green-600">
                              Paid: {new Date(fee.paymentDate).toLocaleDateString('en-IN')}
                            </span>
                            {fee.paymentMethod && (
                              <p className="text-gray-500 capitalize">{fee.paymentMethod}</p>
                            )}
                            {fee.transactionId && (
                              <p className="text-blue-600 text-xs">TXN: {fee.transactionId}</p>
                            )}
                          </div>
                        )}
                        {fee.dueDate && !fee.paymentDate && (
                          <span className="text-gray-500">
                            Due: {new Date(fee.dueDate).toLocaleDateString('en-IN')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 flex space-x-1">
                      {fee.status !== 'paid' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateFeeStatusClick(fee._id, fee, 'paid');
                          }}
                          className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                        >
                          Mark Paid
                        </button>
                      )}
                      {fee.status === 'paid' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateFeeStatus(fee._id, 'pending', null, null);
                          }}
                          className="px-2 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 transition-colors"
                        >
                          Mark Pending
                        </button>
                      )}
                      {fee.status !== 'overdue' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateFeeStatus(fee._id, 'overdue', null, null);
                          }}
                          className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                        >
                          Mark Overdue
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (student) => {
        const feeRecords = student.feeRecords || [];
        const pendingFees = feeRecords.filter(f => f.status === 'pending' || f.status === 'overdue');
        
        return (
          <div className="flex flex-col space-y-1">
            {pendingFees.length > 0 && (
              <button
                onClick={() => {
                  // Calculate total amount for all pending fees
                  const totalAmount = pendingFees.reduce((sum, fee) => sum + (fee.amount || 0), 0);
                  
                  // Show payment modal with total amount
                  setPendingFeeUpdate({ 
                    feeId: pendingFees[0]._id, 
                    feeInfo: {
                      termNumber: 'Multiple Terms',
                      amount: totalAmount,
                      count: pendingFees.length
                    }, 
                    newStatus: 'paid',
                    markAll: true,
                    allFees: pendingFees
                  });
                  setPaymentModalOpen(true);
                }}
                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
              >
                Mark All Paid
              </button>
            )}
            <button
              onClick={() => {
                setSelectedStudent(student);
                setIsDetailModalOpen(true);
              }}
              className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
            >
              View Details
            </button>
          </div>
        );
      }
    }
  ];

  // Filter classes
  const filteredClasses = classes.filter(cls =>
    cls?.name?.toLowerCase().includes(searchTerm?.toLowerCase() || '') ||
    cls?.section?.toLowerCase().includes(searchTerm?.toLowerCase() || '')
  );

  const classTotalPages = Math.ceil(filteredClasses.length / itemsPerPage);
  const classStartIndex = (currentPage - 1) * itemsPerPage;
  const paginatedClasses = filteredClasses.slice(classStartIndex, classStartIndex + itemsPerPage);

  // If class is selected, show students view
  if (selectedClass) {
    return (
      <div className="space-y-8 animate-fadeIn max-w-[1600px] mx-auto">
        {/* Modern Header Section */}
        <div className="glass-card rounded-[2.5rem] p-8 px-10 flex flex-col md:flex-row justify-between items-center gap-6 premium-shadow">
          <div className="flex items-center gap-6">
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleBackToClasses();
              }}
              className="p-4 bg-slate-100 text-[#1e293b] rounded-[1.5rem] hover:bg-slate-200 transition-all duration-300 transform hover:scale-105 shadow-sm"
            >
              <ArrowLeft size={24} className="text-[#1B6CA8]" />
            </button>
            <div className="text-center md:text-left">
              <h1 className="text-4xl font-black tracking-tight text-[#1e293b]">
                {selectedClass.name} <span className="text-[#1B6CA8]">{selectedClass.section}</span>
              </h1>
              <p className="text-slate-500 mt-2 font-medium flex items-center justify-center md:justify-start gap-2">
                <span className="text-[#1B6CA8]">₹</span>
                Manage student fees and payments
              </p>
            </div>
          </div>
          <div className="flex bg-slate-100 rounded-2xl p-1 shadow-sm">
            <button
              onClick={() => {
                const columns = [
                  { header: 'Student Name', dataKey: 'name' },
                  { header: 'Reg No', dataKey: 'regNo' },
                  { header: 'Total Fees', dataKey: 'total' },
                  { header: 'Paid', dataKey: 'paid' },
                  { header: 'Pending', dataKey: 'pending' },
                  { header: 'Status', dataKey: 'status' }
                ];
                const data = filteredStudents.map(s => ({
                  name: s.name,
                  regNo: s.regNo || 'N/A',
                  total: `₹${s.feeSummary?.totalFees || 0}`,
                  paid: `₹${s.feeSummary?.paidFees || 0}`,
                  pending: `₹${s.feeSummary?.pendingFees || 0}`,
                  status: s.feeSummary?.overallStatus || 'pending'
                }));
                ReportService.generatePDF(`Fees - ${selectedClass.name} ${selectedClass.section}`, columns, data, `fees_${selectedClass.name}_${selectedClass.section}.pdf`);
              }}
              className="px-4 py-2 text-[#1e293b] hover:bg-white rounded-xl transition-all duration-200 flex items-center gap-2 font-bold text-sm"
            >
              <Download size={16} className="text-blue-600" />
              <span>PDF</span>
            </button>
            <button
              onClick={() => {
                const data = filteredStudents.map(s => ({
                  'Student Name': s.name,
                  'Reg No': s.regNo || 'N/A',
                  'Total Fees': s.feeSummary?.totalFees || 0,
                  'Paid Fees': s.feeSummary?.paidFees || 0,
                  'Pending Fees': s.feeSummary?.pendingFees || 0,
                  'Overall Status': s.feeSummary?.overallStatus || 'pending'
                }));
                ReportService.generateExcel(data, `fees_${selectedClass.name}_${selectedClass.section}.xlsx`);
              }}
              className="px-4 py-2 text-[#1e293b] hover:bg-white rounded-xl transition-all duration-200 flex items-center gap-2 font-bold text-sm"
            >
              <Download size={16} className="text-emerald-600" />
              <span>Excel</span>
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 animate-slideInUp">
            <div className="glass-card rounded-3xl p-6 premium-shadow border-none flex items-center gap-5">
              <div className="p-4 bg-blue-50 rounded-2xl">
                <IndianRupee className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Fees</p>
                <p className="text-2xl font-black text-slate-800">
                  ₹{statistics.totalFees?.toLocaleString() || 0}
                </p>
              </div>
            </div>

            <div className="glass-card rounded-3xl p-6 premium-shadow border-none flex items-center gap-5">
              <div className="p-4 bg-green-50 rounded-2xl">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1">Paid</p>
                <p className="text-2xl font-black text-green-600">
                  ₹{statistics.statusBreakdown?.find(s => s.status === 'paid')?.totalAmount?.toLocaleString() || 0}
                </p>
              </div>
            </div>

            <div className="glass-card rounded-3xl p-6 premium-shadow border-none flex items-center gap-5">
              <div className="p-4 bg-yellow-50 rounded-2xl">
                <Clock className="w-8 h-8 text-yellow-600" />
              </div>
              <div>
                <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1">Pending</p>
                <p className="text-2xl font-black text-yellow-600">
                  ₹{statistics.statusBreakdown?.find(s => s.status === 'pending')?.totalAmount?.toLocaleString() || 0}
                </p>
              </div>
            </div>

            <div className="glass-card rounded-3xl p-6 premium-shadow border-none flex items-center gap-5">
              <div className="p-4 bg-red-50 rounded-2xl">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1">Overdue</p>
                <p className="text-2xl font-black text-red-600">
                  ₹{statistics.statusBreakdown?.find(s => s.status === 'overdue')?.totalAmount?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="glass-card rounded-[2rem] p-8 premium-shadow border-none animate-slideInUp">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-8 gap-4">
            <div>
              <h3 className="text-xl font-black text-slate-800 tracking-tight">Filters</h3>
              <p className="text-slate-400 text-sm font-medium">Refine your fee management view</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            <div>
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Academic Year</label>
              <input
                type="text"
                value={selectedAcademicYearFilter}
                onChange={(e) => setSelectedAcademicYearFilter(e.target.value)}
                placeholder="2024-2025"
                className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none transition-all duration-300 font-semibold text-slate-700"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Search Name</label>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none transition-all duration-300 font-semibold text-slate-700"
              />
            </div>
            {/* Gender Filter */}
            <div>
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Gender</label>
              <select
                value={selectedGenderFilter}
                onChange={(e) => setSelectedGenderFilter(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none transition-all duration-300 font-semibold text-slate-700 cursor-pointer"
              >
                <option value="">All Genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Sort Order</label>
              <select
                value={nameSort}
                onChange={(e) => setNameSort(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none transition-all duration-300 font-semibold text-slate-700 cursor-pointer"
              >
                <option value="a-z">Name (A to Z)</option>
                <option value="z-a">Name (Z to A)</option>
              </select>
            </div>
            <div className="flex items-end xl:col-start-5">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedGenderFilter('');
                  setNameSort('a-z');
                }}
                className="w-full px-4 py-3 text-slate-500 font-bold bg-slate-100/50 rounded-xl hover:bg-slate-200/50 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <X size={18} />
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Students Table */}
        <div className="bg-white rounded-xl shadow-sm border animate-slideInUp">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="text-sm text-gray-600">
                Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredStudents.length)} of {filteredStudents.length} students
              </div>
            </div>
          </div>

          <Table
            data={paginatedStudents}
            columns={studentColumns}
            searchable={false}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>

        <FeeDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedStudent(null);
          }}
          student={selectedStudent}
          onUpdate={() => {
            loadStudentsWithFees();
            loadStatistics();
          }}
        />

        <PaymentTypeModal
          isOpen={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setPendingFeeUpdate(null);
          }}
          onConfirm={handlePaymentConfirm}
          feeInfo={pendingFeeUpdate?.feeInfo}
        />

        <Loader 
          isLoading={isLoading} 
          message="Loading fees data..." 
          size="large" 
          overlay={true} 
        />
      </div>
    );
  }

  // Classes List View
  return (
    <div className="space-y-8 animate-fadeIn max-w-[1600px] mx-auto">
      {/* Modern Header Section */}
      <div className="glass-card rounded-[2.5rem] p-8 px-10 flex flex-col md:flex-row justify-between items-center gap-6 premium-shadow">
        <div className="text-center md:text-left">
          <h1 className="text-4xl font-black tracking-tight">
            <span className="bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
              Fees Management
            </span>
          </h1>
          <p className="text-slate-500 mt-2 font-medium flex items-center justify-center md:justify-start gap-2">
            <IndianRupee size={18} className="text-[#1B6CA8]" />
            Select a class to manage student fees
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          <div className="flex bg-slate-100 rounded-2xl p-1 shadow-sm">
            <button
              onClick={() => {
                const columns = [
                  { header: 'Class', dataKey: 'name' },
                  { header: 'Section', dataKey: 'section' },
                  { header: 'Fee/Year', dataKey: 'fees' },
                  { header: 'Terms', dataKey: 'terms' },
                  { header: 'Year', dataKey: 'year' }
                ];
                const data = filteredClasses.map(cls => ({
                  name: cls.name,
                  section: cls.section,
                  fees: `Rs. ${cls.feesPerAcademicYear || 0}`,
                  terms: cls.numberOfTerms || 0,
                  year: `${new Date(cls.academicYearFrom).getFullYear()}-${new Date(cls.academicYearTo).getFullYear()}`
                }));
                ReportService.generatePDF('Fees Structure', columns, data, 'fees_structure.pdf');
              }}
              className="px-4 py-2 text-[#1e293b] hover:bg-white rounded-xl transition-all duration-200 flex items-center gap-2 font-bold text-sm"
            >
              <Download size={16} className="text-blue-600" />
              <span>PDF</span>
            </button>
            <button
              onClick={() => {
                const data = filteredClasses.map(cls => ({
                  Class: cls.name,
                  Section: cls.section,
                  'Fee Per Year': cls.feesPerAcademicYear || 0,
                  'Number of Terms': cls.numberOfTerms || 0,
                  'Academic Year': `${new Date(cls.academicYearFrom).getFullYear()}-${new Date(cls.academicYearTo).getFullYear()}`
                }));
                ReportService.generateExcel(data, 'fees_structure.xlsx');
              }}
              className="px-4 py-2 text-[#1e293b] hover:bg-white rounded-xl transition-all duration-200 flex items-center gap-2 font-bold text-sm"
            >
              <Download size={16} className="text-emerald-600" />
              <span>Excel</span>
            </button>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="primary-gradient-bg text-white px-8 py-3.5 rounded-2xl hover:opacity-90 transition-all duration-300 flex items-center gap-3 font-bold shadow-lg shadow-blue-200/50 hover:scale-[1.02] active:scale-[0.98]"
          >
            <Plus size={24} />
            <span>Create Fees</span>
          </button>
        </div>
      </div>

      {/* Classes Table */}
      <div className="bg-white rounded-xl shadow-sm border animate-slideInUp">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="text-sm text-gray-600">
              Showing {classStartIndex + 1}-{Math.min(classStartIndex + itemsPerPage, filteredClasses.length)} of {filteredClasses.length} classes
            </div>
          </div>
        </div>

        <Table
          data={paginatedClasses}
          columns={classColumns}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          currentPage={currentPage}
          totalPages={classTotalPages}
          onPageChange={setCurrentPage}
          searchable={true}
        />
      </div>

      <CreateFeesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          loadClasses();
        }}
      />
    </div>
  );
};

export default Fees;
